import { fetchApi } from "@/api/fetchApi";
import _cloneDeep from "lodash/cloneDeep";
import _filter from "lodash/filter";
import _find from "lodash/find";
import _intersection from "lodash/intersection";

import releaseNoteApi from "../api/releaseNote";

const operators = {
  "=": ({ filterValue, recordValue }) => {
    return `${filterValue}`.toLowerCase() === `${recordValue}`.toLowerCase();
  },
  ">": ({ filterValue, recordValue }) => recordValue > filterValue,
  ">=": ({ filterValue, recordValue }) => recordValue >= filterValue,
  "<": ({ filterValue, recordValue }) => recordValue < filterValue,
  "<=": ({ filterValue, recordValue }) => recordValue <= filterValue,
  contains: ({ filterValue, recordValue }) => `${recordValue}`.toLowerCase().includes(`${filterValue}`.toLowerCase()),
  csv: ({ filterValue, recordValue }) => {
    if (Array.isArray(filterValue)) {
      return filterValue.includes(recordValue);
    }

    return `${filterValue}` === `${recordValue}`;
  },
  excludes: ({ filterValue, recordValue }) => !`${recordValue}`.toLowerCase().includes(`${filterValue}`.toLowerCase()),
  in: ({ filterValue, recordValue }) => {
    if (typeof filterValue === "undefined") {
      return;
    }

    if (typeof recordValue === "undefined") {
      return;
    }

    if ((typeof recordValue === "string" || recordValue === null) && Array.isArray(filterValue)) {
      return filterValue.includes(recordValue ? `${recordValue}` : recordValue);
    }

    if (Array.isArray(recordValue) && Array.isArray(filterValue)) {
      const intersection = _intersection(filterValue, recordValue);
      return intersection.length > 0;
    }

    if (typeof recordValue === "number" && (Array.isArray(filterValue) || typeof filterValue === "string")) {
      return filterValue.includes(recordValue);
    }

    if (recordValue === null) {
      return;
    }

    return recordValue.includes(filterValue);
  },
  notIn: ({ filterValue, recordValue }) => {
    return !operators.in({ filterValue, recordValue });
  },
};

const releaseNoteTemplate = {
  activitiesImpacted: "",
  addedPostUat: 0,
  background: "",
  buildJira: null,
  currentBehavior: "",
  datePublished: null,
  documentedOnBehalfOf: null,
  documentationStatus: "In Process",
  applicationDropdownValueId: null,
  individualSetup: [],
  individualSetupId: null,
  isGovernedTemplate: 0,
  isPublished: 0,
  isPulled: 0,
  issue: "",
  newBehavior: "",
  note: null,
  otherUserRoles: null,
  optionalEnhancement: "",
  packId: 0,
  preGoLiveSetup: "",
  primaryActivity: "",
  primaryActivityDropdownValueId: null,
  pulledDate: null,
  pulledNextSteps: null,
  pulledReason: null,
  qaJira: null,
  reopenedReason: "",
  requiredSetup: "",
  sourceTemplateId: null,
  status: "Open",
  summary: "",
  templateType: null,
  title: null,
  trainingJira: null,
  updatedReason: "",
  userRole: null,
};

const richTextFieldList = [
  "background",
  "currentBehavior",
  "issue",
  "newBehavior",
  "note",
  "optionalEnhancement",
  "preGoLiveSetup",
  "requiredSetup",
  "summary",
  "updatedReason",
];

function prepareTestSetupItems(items) {
  return items.map(item => {
    delete item.index;
    return item;
  });
}

const actions = {
  async batchEditTestPlan({ commit }, { serviceAreaId, data }) {
    await releaseNoteApi.batchEditTestPlan({ serviceAreaId, data });
  },
  async batchEditReleaseNote({ commit }, { data }) {
    await releaseNoteApi.batchEditReleaseNote({ data });
  },
  async create({ state }) {
    const releaseNote = state.releaseNoteRecord;
    releaseNote.individualSetup = prepareTestSetupItems(releaseNote.individualSetup);
    return releaseNoteApi.post(releaseNote);
  },
  async deleteRecord({ commit }, id) {
    await releaseNoteApi.deleteRecord(id);
  },
  async getReleaseNote({ commit }, routeParams) {
    const releaseNoteId = routeParams.id;
    const releaseNote = await releaseNoteApi.get(releaseNoteId);
    return commit("setReleaseNote", releaseNote.data.result);
  },
  async getReleaseNoteList({ commit }, routeParams) {
    const packId = routeParams.id;
    const releaseNoteList = await releaseNoteApi.list(packId);
    return commit("setReleaseNoteList", releaseNoteList.data.result);
  },
  async getReleaseNoteRecord({ commit }, releaseNoteId) {
    const releaseNoteRecord = await releaseNoteApi.get(releaseNoteId);
    return commit("setReleaseNoteRecord", releaseNoteRecord.data.result);
  },
  async getNav({ commit, state, dispatch }, params) {
    const { releaseNoteId, sortBy, sortDesc } = params;

    let { searchContext } = params;
    if (!searchContext) {
      searchContext = "global";
    }

    let offset = 0;
    let rowCount = 100;

    let searchIndex;

    do {
      await dispatch("getSearchReleaseNoteList", { searchContext, rowCount, offset, sortBy, sortDesc });
      searchIndex = state.searchReleaseNoteList.findIndex(note => {
        return note.releaseNoteId === releaseNoteId;
      });

      if (offset > state.searchTotalCount - 1) {
        // Early return to prevent infinite loop
        return;
      }

      offset += rowCount;
    } while (searchIndex < 0);

    searchIndex += offset - rowCount;

    let nextNoteId = null;
    let prevNoteId = null;

    if (searchIndex > 0) {
      offset = searchIndex - 1;
      await dispatch("getSearchReleaseNoteList", { searchContext, rowCount: 1, offset, sortBy, sortDesc });

      prevNoteId = state.searchReleaseNoteList[0];
    }
    if (searchIndex < state.searchTotalCount - 1) {
      offset = searchIndex + 1;
      await dispatch("getSearchReleaseNoteList", { searchContext, rowCount: 1, offset, sortBy, sortDesc });

      nextNoteId = state.searchReleaseNoteList[0];
    }

    const navigationOptionMap = {
      listLength: state.searchTotalCount,
      positionInList: searchIndex,
      prev: prevNoteId,
      next: nextNoteId,
    };

    commit("setNavigationOptionMap", navigationOptionMap);
  },
  async getSearchReleaseNoteList({ commit, rootState }, params) {
    const { offset, rowCount, sortBy, sortDesc } = params;

    let { searchContext } = params;

    if (!searchContext) {
      searchContext = "global";
    }

    const userConfiguration = rootState.session.user.userConfiguration;
    const filterList = userConfiguration.widgets.releaseNoteList.filters[searchContext] || [];
    const filterOverrideList = userConfiguration.filterOverrideList || [];

    const packFilters = [];
    if (searchContext != "global") {
      packFilters.push({
        attributeName: "packId",
        dataType: "pack",
        operator: "in",
        value: [searchContext],
        valueType: "array",
        attributeNameLabel: "Packs",
        operatorLabel: "=",
      });
    }

    commit("setReleaseNoteSearchLoader", true);
    const result = await fetchApi({
      url: "/api/search",
      method: "post",
      data: {
        filterList: [...filterList, ...filterOverrideList, ...packFilters],
        offset,
        rowCount,
        sortBy,
        sortDesc,
      },
    });
    commit("setReleaseNoteSearchLoader", false);
    commit("setSearchReleaseNoteData", result.data);
  },
  async getTestPlan({ commit }, { releaseNoteId, serviceArea }) {
    const testPlan = await releaseNoteApi.getTestPlan(releaseNoteId, serviceArea);
    return commit("setTestPlan", testPlan.data.result);
  },
  async getTestPlanRecord({ commit }, { releaseNoteId, serviceAreaId }) {
    const testPlanRecord = await releaseNoteApi.getTestPlan(releaseNoteId, serviceAreaId);
    return commit("setTestPlanRecord", testPlanRecord.data.result);
  },
  async getTestPlanList({ commit }, packId) {
    commit("setTestPlanLoader", true);
    const testPlanList = await releaseNoteApi.getTestPlanList(packId);
    commit("setTestPlanLoader", false);
    return commit("setTestPlanList", testPlanList.data.result);
  },
  async resetReleaseNoteRecord({ commit }, packId) {
    commit("resetReleaseNoteRecord", packId);
  },
  async setReleaseNoteIndividualSetup({ commit }, individualSetup) {
    commit("setReleaseNoteIndividualSetup", individualSetup);
  },
  async update({ state }) {
    const releaseNote = state.releaseNoteRecord;
    releaseNote.individualSetup = prepareTestSetupItems(releaseNote.individualSetup);
    return releaseNoteApi.put(releaseNote);
  },
  async updateIndividualSetup({ commit }, { releaseNoteId, testSetupList }) {
    const individualSetup = prepareTestSetupItems(testSetupList);
    const releaseNoteRecord = await releaseNoteApi.putIndividualSetup(releaseNoteId, individualSetup);
    commit("setReleaseNoteRecord", releaseNoteRecord);
  },
  async updateTestPlan(context, testPlan) {
    return releaseNoteApi.putTestPlan(testPlan);
  },
  async updateAssignTo(context, { assignedTo, releaseNoteId, serviceArea }) {
    const testPlanResults = await releaseNoteApi.getTestPlan(releaseNoteId, serviceArea);
    const testPlan = testPlanResults.data.result;
    testPlan.assignedTo = assignedTo;
    return releaseNoteApi.putTestPlan(testPlan);
  },
  async updateReleaseNoteListPage({ commit }, lastPage) {
    commit("setReleaseNoteListPage", lastPage);
  },
};

const mutations = {
  resetReleaseNoteRecord(state, packId) {
    const template = Object.assign({}, releaseNoteTemplate);
    template.packId = parseInt(packId, 10);
    state.releaseNoteRecord = template;
  },
  setNavigationOptionMap(state, navigationOptionMap) {
    state.navigationOptionMap = navigationOptionMap;
  },
  setReleaseNote(state, releaseNote) {
    state.releaseNoteRecord = releaseNote;
  },
  setReleaseNoteIndividualSetup(state, individualSetup) {
    state.releaseNote.individualSetup = individualSetup;
  },
  setReleaseNoteList(state, releaseNoteList) {
    state.releaseNoteList = releaseNoteList;
  },
  setReleaseNoteListPage(state, lastPage) {
    state.releaseNoteListPage = lastPage;
  },
  setReleaseNoteRecord(state, releaseNoteRecord) {
    state.releaseNoteRecord = Object.assign({}, releaseNoteRecord);
  },
  setReleaseNoteSearchLoader(state, isLoading) {
    state.isLoadingReleaseNoteSearch = isLoading;
  },
  setReleaseNoteTitle(state, title) {
    state.releaseNoteRecord.title = title;
  },
  setSearchReleaseNoteData(state, { result, totalCount }) {
    state.searchReleaseNoteList = result;
    state.searchTotalCount = totalCount;
  },
  setTestPlan(state, testPlan) {
    state.testPlan = testPlan;
  },
  setTestPlanBatchEditSelectionList(state, selectionList) {
    state.testPlanBatchEditSelectionList = selectionList;
  },
  setReleaseNoteBatchEditSelectionList(state, selectionList) {
    state.releaseNoteBatchEditSelectionList = selectionList;
  },
  setTestPlanRecord(state, testPlanRecord) {
    state.testPlanRecord = Object.assign({}, testPlanRecord);
  },
  setTestPlanList(state, testPlanList) {
    state.testPlanList = testPlanList;
  },
  setTestPlanLoader(state, isLoading) {
    state.isLoadingTestPlanList = isLoading;
  },
  setTestStepList(state, testStepList) {
    state.testStepList = testStepList;
  },
};

const getters = {};

const state = {
  isLoadingTestPlanList: false,
  isLoadingTestStepList: false,
  isLoadingReleaseNoteSearch: false,
  navigationOptionMap: {},
  releaseNote: {},
  releaseNoteList: [],
  releaseNoteListPage: 1,
  releaseNoteRecord: {},
  richTextFieldList,
  searchReleaseNoteList: [],
  searchTotalCount: 0,
  testPlan: {},
  testPlanBatchEditSelectionList: [],
  releaseNoteBatchEditSelectionList: [],
  testPlanList: [],
  testPlanRecord: {},
  testStepList: [],
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  operators,
  state,
};
